import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { createDirectionNote, getDirectionNotes } from '../../api/clientPortal';
import { useClientTheme } from '../../contexts/ClientThemeContext';
import { Spinner } from './ClientDashboardPage';

const TAGS = ['color', 'silhouette', 'fabric', 'reference', 'fit', 'finish'];

export default function ClientDirectionPage() {
  const { theme: T } = useClientTheme();
  const [body, setBody] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [pageError, setPageError] = useState('');

  const { data, isLoading, refetch } = useQuery(['client-direction-notes'], getDirectionNotes);
  const notes = useMemo(() => (Array.isArray(data?.notes) ? data.notes : []), [data]);

  const toggleTag = (tag) => setSelectedTags((prev) => prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]);

  const submit = async (e) => {
    e.preventDefault();
    setPageError('');
    if (!body.trim()) { setPageError('Direction text is required.'); return; }
    try {
      setSubmitting(true);
      await createDirectionNote({ body: body.trim(), tags: selectedTags, attachmentUrls: imageUrl.trim() ? [imageUrl.trim()] : [] });
      setBody(''); setImageUrl(''); setSelectedTags([]);
      await refetch();
    } catch (err) {
      setPageError(err?.response?.data?.error || 'Failed to submit note.');
    } finally { setSubmitting(false); }
  };

  const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 8, border: `1px solid ${T.inputBorder}`, background: T.inputBg, color: T.inputColor, fontSize: 13, outline: 'none' };
  const card = { borderRadius: 10, border: `1px solid ${T.border}`, background: T.surface, padding: 18 };
  const sLabel = { fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.label, marginBottom: 14 };

  return (
    <div>
      <div style={{ fontSize: 20, fontWeight: 700, color: T.text, marginBottom: 20 }}>Design Direction</div>

      <div style={{ ...card, marginBottom: 24 }}>
        <div style={sLabel}>New Direction Note</div>
        {pageError && <div style={{ padding: '8px 12px', borderRadius: 7, border: `1px solid ${T.errorBorder}`, background: T.errorBg, color: T.errorText, fontSize: 12, marginBottom: 12 }}>{pageError}</div>}

        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <textarea placeholder="Share direction on color story, silhouettes, fit, fabrics, or references…" value={body} onChange={(e) => setBody(e.target.value)} rows={4} style={{ ...inputStyle, resize: 'vertical' }} />
          <input type="url" placeholder="Reference image URL (optional)" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} style={inputStyle} />

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
            {TAGS.map((tag) => {
              const active = selectedTags.includes(tag);
              return (
                <button key={tag} type="button" onClick={() => toggleTag(tag)} style={{ padding: '4px 12px', borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', border: active ? `1px solid ${T.gold}` : `1px solid ${T.inputBorder}`, background: active ? 'rgba(201,168,76,0.14)' : T.inputBg, color: active ? T.gold : T.textSub }}>
                  {tag}
                </button>
              );
            })}
          </div>

          <button type="submit" disabled={submitting} style={{ alignSelf: 'flex-start', padding: '9px 20px', borderRadius: 8, border: `1px solid ${T.gold}`, background: T.gold, color: '#0e0e0e', fontWeight: 700, fontSize: 12, letterSpacing: '0.04em', cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1 }}>
            {submitting ? 'Submitting…' : 'Submit Note'}
          </button>
        </form>
      </div>

      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.label, marginBottom: 12 }}>History</div>
      {isLoading ? <Spinner T={T} /> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {notes.map((note) => (
            <div key={note._id || note.id} style={{ borderRadius: 10, border: `1px solid ${T.border}`, background: T.surface, padding: 14 }}>
              <div style={{ fontSize: 13, color: T.text, lineHeight: 1.55, marginBottom: 8 }}>{note.body}</div>
              {(note.tags || []).length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                  {note.tags.map((tag) => (
                    <span key={`${note._id}-${tag}`} style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', padding: '2px 8px', borderRadius: 999, border: `1px solid rgba(201,168,76,0.28)`, background: 'rgba(201,168,76,0.08)', color: T.gold }}>{tag}</span>
                  ))}
                </div>
              )}
              {(note.attachmentUrls || []).map((url) => (
                <div key={url} style={{ fontSize: 11, marginBottom: 4 }}>
                  <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: T.gold, textDecoration: 'none' }}>{url}</a>
                </div>
              ))}
              <div style={{ fontSize: 11, color: T.textMuted }}>{note.createdAt ? new Date(note.createdAt).toLocaleString() : '—'}</div>
            </div>
          ))}
          {!notes.length && <div style={{ padding: '20px 0', textAlign: 'center', fontSize: 13, color: T.textMuted }}>No direction notes yet.</div>}
        </div>
      )}
    </div>
  );
}
